import { useState, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { ArrowLeft, Clock, Trophy, AlertTriangle, Shield, Swords, Crown, Calendar, ChevronDown, ChevronUp, Users, Eye, EyeOff, Copy, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Tournament, MobaTeam, MobaBracketData, MobaGroupMatch, MobaKnockoutMatch } from '../types';
import { useModal } from '../context/ModalContext';
import { useWalletLock } from '../context/WalletLockContext';
import TCoinIcon from '../components/TCoinIcon';
import GameRulesCard from '../components/GameRulesCard';
import { useTournamentEntries, callLeaveTournament, useHasJoined, callJoinTournament, useMySquads } from '../services/tournamentService';
import { useAuth } from '../context/AuthContext';
import { useWalletData } from '../services/walletService';

// ─── Interfaces ─────────────────────────────────────────────────────────────

interface Props {
    tournament: Tournament;
}

// ─── Helper Functions ───────────────────────────────────────────────────────

function getTimeLeft(start: string, status?: string) {
    const diff = new Date(start).getTime() - Date.now();
    if (status === 'live') return 'In Progress';
    if (status === 'completed') return 'Ended';
    if (diff <= 0) return 'Starting soon';
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    if (d > 0) return `${d}d ${h}h`;
    return `${h}h ${m}m`;
}

function formatMatchDateTime(date: string, time: string) {
    const d = new Date(date + 'T' + time);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' • ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

function getTeamById(teams: MobaTeam[], id: string | null): MobaTeam | null {
    if (!id) return null;
    return teams.find(t => t.id === id) || null;
}

// ─── Team Card Component ────────────────────────────────────────────────────

function TeamCard({ team, isWinner, isLoser, size = 'normal' }: { team: MobaTeam | null; isWinner?: boolean; isLoser?: boolean; size?: 'normal' | 'small' }) {
    if (!team) {
        return (
            <div style={{
                padding: size === 'small' ? '6px 10px' : '8px 14px',
                background: 'rgba(148,163,184,0.04)',
                border: '1px dashed rgba(148,163,184,0.2)',
                borderRadius: 8,
                display: 'flex', alignItems: 'center', gap: 8,
                opacity: 0.5,
            }}>
                <div style={{ width: size === 'small' ? 24 : 28, height: size === 'small' ? 24 : 28, borderRadius: 6, background: 'rgba(148,163,184,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: size === 'small' ? '0.6rem' : '0.7rem', color: 'var(--text-muted)' }}>?</span>
                </div>
                <span style={{ fontSize: size === 'small' ? '0.75rem' : '0.85rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>TBD</span>
            </div>
        );
    }
    return (
        <div style={{
            padding: size === 'small' ? '6px 10px' : '8px 14px',
            background: isWinner ? 'rgba(16,185,129,0.08)' : isLoser ? 'rgba(239,68,68,0.04)' : 'rgba(148,163,184,0.04)',
            border: isWinner ? '1px solid rgba(16,185,129,0.3)' : isLoser ? '1px solid rgba(239,68,68,0.15)' : '1px solid var(--border)',
            borderRadius: 8,
            display: 'flex', alignItems: 'center', gap: 8,
            opacity: isLoser ? 0.5 : 1,
            transition: 'all 0.2s',
        }}>
            <div style={{
                width: size === 'small' ? 24 : 28, height: size === 'small' ? 24 : 28, borderRadius: 6,
                background: `${team.color}20`, border: `1px solid ${team.color}40`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: size === 'small' ? '0.55rem' : '0.65rem', fontWeight: 800, color: team.color,
            }}>
                {team.tag}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: size === 'small' ? '0.78rem' : '0.88rem', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {team.name}
                </div>
                {size === 'normal' && (
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{team.rank} • {team.region}</div>
                )}
            </div>
            {isWinner && <Crown size={14} style={{ color: 'var(--success)', flexShrink: 0 }} />}
        </div>
    );
}

// ─── Match Row Component ────────────────────────────────────────────────────

function MatchRow({ match, teams, type }: { match: MobaGroupMatch | MobaKnockoutMatch; teams: MobaTeam[]; type: 'group' | 'knockout' }) {
    const team1 = getTeamById(teams, match.team1Id);
    const team2 = getTeamById(teams, match.team2Id);
    const isDone = match.winnerId !== null;
    const roundLabel = type === 'group' ? (match as MobaGroupMatch).roundLabel : (match as MobaKnockoutMatch).label;
    const isBo3 = type === 'knockout' && 'bestOf' in match && (match as MobaKnockoutMatch).bestOf === 3;
    const koMatch = type === 'knockout' ? (match as MobaKnockoutMatch) : null;

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
                padding: '12px 16px',
                background: isDone ? 'rgba(16,185,129,0.03)' : 'var(--bg-card)',
                border: isDone ? '1px solid rgba(16,185,129,0.15)' : '1px solid var(--border)',
                borderRadius: 10, marginBottom: 8,
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--accent-hover)', textTransform: 'uppercase', letterSpacing: 1 }}>
                    {roundLabel}
                    {isBo3 && <span style={{ marginLeft: 6, fontSize: '0.62rem', padding: '1px 6px', borderRadius: 4, background: 'rgba(139,92,246,0.1)', color: '#8b5cf6', fontWeight: 700 }}>Bo3</span>}
                </span>
                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Calendar size={10} />
                    {formatMatchDateTime(match.scheduledDate, match.scheduledTime)}
                </span>
            </div>
            <div className="mobile-match-stack" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div className="mobile-match-team" style={{ flex: 1 }}>
                    <TeamCard team={team1} isWinner={isDone && match.winnerId === match.team1Id} isLoser={isDone && match.winnerId !== match.team1Id} size="small" />
                </div>
                <div style={{
                    width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                    background: isDone ? 'rgba(16,185,129,0.1)' : 'rgba(99,102,241,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    {isDone ? <Crown size={14} style={{ color: 'var(--success)' }} /> : <Swords size={14} style={{ color: 'var(--accent-hover)' }} />}
                </div>
                <div className="mobile-match-team" style={{ flex: 1 }}>
                    <TeamCard team={team2} isWinner={isDone && match.winnerId === match.team2Id} isLoser={isDone && match.winnerId !== match.team2Id} size="small" />
                </div>
            </div>
            {isDone && (
                <div style={{ marginTop: 8, textAlign: 'center', fontSize: '0.72rem', color: 'var(--success)', fontWeight: 600 }}>
                    ✓ {getTeamById(teams, match.winnerId)?.name} advances
                </div>
            )}
            {/* Single-match MVP (group matches or non-Bo3 knockout) */}
            {isDone && match.mvpPlayerName && !isBo3 && (
                <div style={{
                    marginTop: 6, textAlign: 'center', padding: '4px 10px', borderRadius: 6,
                    background: 'rgba(245,158,11,0.1)', display: 'inline-flex',
                    alignItems: 'center', gap: 4, fontSize: '0.7rem', fontWeight: 600,
                    color: '#f59e0b', width: '100%', justifyContent: 'center',
                }}>
                    🌟 MVP: {match.mvpPlayerName} ({getTeamById(teams, match.winnerId)?.name})
                </div>
            )}
            {/* Bo3 per-game results */}
            {isDone && isBo3 && koMatch?.games && koMatch.games.length > 0 && (
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {koMatch.games.filter(g => g.winnerId).map(g => {
                        const gWinner = getTeamById(teams, g.winnerId);
                        return (
                            <div key={g.gameNumber} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                padding: '3px 10px', borderRadius: 6,
                                background: 'rgba(148,163,184,0.04)', border: '1px solid var(--border)',
                                fontSize: '0.68rem', color: 'var(--text-secondary)',
                            }}>
                                <span style={{ fontWeight: 700, color: 'var(--text-muted)' }}>G{g.gameNumber}</span>
                                <span style={{ color: 'var(--success)', fontWeight: 600 }}>🏆 {gWinner?.tag || 'TBD'}</span>
                                {g.mvpPlayerName && (
                                    <span style={{ color: '#f59e0b', fontWeight: 600 }}>🌟 {g.mvpPlayerName}</span>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </motion.div>
    );
}

// ─── Group Card Component ───────────────────────────────────────────────────

function MobaGroupCard({ group, teams, matches }: { group: { label: string; teamIds: string[] }; teams: MobaTeam[]; matches: MobaGroupMatch[] }) {
    const [expanded, setExpanded] = useState(false);
    const groupTeams = group.teamIds.map(id => getTeamById(teams, id)).filter(Boolean) as MobaTeam[];
    const groupMatches = matches.filter(m => m.groupLabel === group.label);
    const groupWinner = groupMatches.find(m => m.roundLabel === 'Group Final')?.winnerId;
    const winnerTeam = groupWinner ? getTeamById(teams, groupWinner) : null;

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ layout: { duration: 0.3, ease: 'easeInOut' } }}
            className="card moba-group-card"
            style={{ padding: '16px 18px', position: 'relative', overflow: 'hidden' }}
        >
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${groupTeams[0]?.color || 'var(--accent)'}60, ${groupTeams[groupTeams.length - 1]?.color || 'var(--accent)'}60)` }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, paddingTop: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                        width: 32, height: 32, borderRadius: 8,
                        background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 800, fontSize: '0.9rem', color: 'var(--accent-hover)',
                    }}>
                        {group.label}
                    </div>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>Group {group.label}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{groupTeams.length} Teams</div>
                    </div>
                </div>
                {winnerTeam && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', background: 'rgba(16,185,129,0.08)', borderRadius: 6, border: '1px solid rgba(16,185,129,0.2)' }}>
                        <Crown size={12} style={{ color: 'var(--success)' }} />
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--success)' }}>{winnerTeam.tag}</span>
                    </div>
                )}
            </div>

            {/* Teams */}
            <div className="moba-group-teams-grid mobile-grid-1-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 10 }}>
                {groupTeams.map(team => (
                    <TeamCard key={team.id} team={team} isWinner={team.id === groupWinner} size="small" />
                ))}
            </div>

            {/* Expand matches */}
            <button
                onClick={() => setExpanded(!expanded)}
                style={{
                    width: '100%', padding: '6px 0', background: 'none', border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                    fontSize: '0.78rem', color: 'var(--accent-hover)', fontWeight: 600,
                }}
            >
                {expanded ? 'Hide' : 'View'} Matches ({groupMatches.length})
                {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>

            <AnimatePresence>
                {expanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        style={{ overflow: 'hidden', marginTop: 8 }}
                    >
                        {groupMatches.map(match => (
                            <MatchRow key={match.id} match={match} teams={teams} type="group" />
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}

// ─── Knockout Bracket Diagram ───────────────────────────────────────────────

function KnockoutBracket({ bracket, teams }: { bracket: MobaBracketData; teams: MobaTeam[] }) {
    const knockoutMatches = bracket.knockoutMatches;
    const groupCount = bracket.groups.length;

    // For 4 groups: left SF, right SF, final
    // For 2 groups: just final
    const leftSF = knockoutMatches.find(m => m.label === 'Semifinal 1');
    const rightSF = knockoutMatches.find(m => m.label === 'Semifinal 2');
    const final = knockoutMatches.find(m => m.label === 'Grand Final');

    if (!final) return null;

    const finalTeam1 = getTeamById(teams, final.team1Id);
    const finalTeam2 = getTeamById(teams, final.team2Id);
    const champion = final.winnerId ? getTeamById(teams, final.winnerId) : null;

    // Box style for knockout
    const koBoxStyle = (team: MobaTeam | null, isWinner: boolean): React.CSSProperties => ({
        padding: '10px 14px', borderRadius: 10, minWidth: 140,
        background: isWinner ? 'rgba(16,185,129,0.08)' : team ? 'rgba(148,163,184,0.04)' : 'rgba(148,163,184,0.02)',
        border: isWinner ? '2px solid rgba(16,185,129,0.4)' : team ? '1px solid var(--border)' : '1px dashed rgba(148,163,184,0.2)',
        display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.3s',
    });

    const tagBadge = (team: MobaTeam | null) => team ? (
        <div style={{ width: 28, height: 28, borderRadius: 6, background: `${team.color}20`, border: `1px solid ${team.color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 800, color: team.color }}>{team.tag}</div>
    ) : (
        <div style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(148,163,184,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', color: 'var(--text-muted)' }}>?</div>
    );

    if (groupCount === 2) {
        // Simple: Group A winner vs Group B winner → Final → Champion
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
                {/* Header */}
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--accent-hover)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 4 }}>Grand Final</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{formatMatchDateTime(final.scheduledDate, final.scheduledTime)}</div>
                </div>

                {/* Match */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={koBoxStyle(finalTeam1, final.winnerId === final.team1Id)}>
                        {tagBadge(finalTeam1)}
                        <div>
                            <div style={{ fontSize: '0.85rem', fontWeight: 700 }}>{finalTeam1?.name || 'TBD'}</div>
                            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Winner Group A</div>
                        </div>
                        {final.winnerId === final.team1Id && <Crown size={14} style={{ color: 'var(--success)' }} />}
                    </div>
                    <div style={{
                        width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                        background: 'linear-gradient(135deg, rgba(245,158,11,0.15), rgba(99,102,241,0.15))',
                        border: '2px solid rgba(245,158,11,0.3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Swords size={16} style={{ color: 'var(--warning)' }} />
                    </div>
                    <div style={koBoxStyle(finalTeam2, final.winnerId === final.team2Id)}>
                        {tagBadge(finalTeam2)}
                        <div>
                            <div style={{ fontSize: '0.85rem', fontWeight: 700 }}>{finalTeam2?.name || 'TBD'}</div>
                            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Winner Group B</div>
                        </div>
                        {final.winnerId === final.team2Id && <Crown size={14} style={{ color: 'var(--success)' }} />}
                    </div>
                </div>

                {/* Champion */}
                {champion && (
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        style={{
                            textAlign: 'center', padding: '16px 24px', borderRadius: 12,
                            background: 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(16,185,129,0.08))',
                            border: '2px solid rgba(245,158,11,0.3)',
                        }}
                    >
                        <div style={{ fontSize: '1.5rem', marginBottom: 4 }}>🏆</div>
                        <div style={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '1.1rem', color: 'var(--warning)' }}>{champion.name}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>Tournament Champion</div>
                    </motion.div>
                )}
            </div>
        );
    }

    // 4-group layout
    const leftSFTeam1 = leftSF ? getTeamById(teams, leftSF.team1Id) : null;
    const leftSFTeam2 = leftSF ? getTeamById(teams, leftSF.team2Id) : null;
    const rightSFTeam1 = rightSF ? getTeamById(teams, rightSF.team1Id) : null;
    const rightSFTeam2 = rightSF ? getTeamById(teams, rightSF.team2Id) : null;

    return (
        <div style={{ position: 'relative' }}>
            {/* SVG connectors */}
            <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0 }}>
                {/* Lines will be drawn using CSS borders instead for simplicity */}
            </svg>

            <div className="knockout-bracket-grid" style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 20, alignItems: 'center' }}>
                {/* Left Column: Left Semifinal */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ textAlign: 'center', fontSize: '0.72rem', fontWeight: 700, color: 'var(--accent-hover)', textTransform: 'uppercase', letterSpacing: 1.5 }}>
                        Semifinal 1
                    </div>
                    {leftSF && (
                        <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textAlign: 'center' }}>
                            {formatMatchDateTime(leftSF.scheduledDate, leftSF.scheduledTime)}
                        </div>
                    )}
                    <div style={koBoxStyle(leftSFTeam1, leftSF?.winnerId === leftSF?.team1Id)}>
                        {tagBadge(leftSFTeam1)}
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.82rem', fontWeight: 700 }}>{leftSFTeam1?.name || 'TBD'}</div>
                            <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>Winner Group A</div>
                        </div>
                        {leftSF?.winnerId === leftSF?.team1Id && leftSF?.winnerId && <Crown size={12} style={{ color: 'var(--success)' }} />}
                    </div>
                    <div style={{ textAlign: 'center', fontSize: '0.68rem', color: 'var(--text-muted)' }}>VS</div>
                    <div style={koBoxStyle(leftSFTeam2, leftSF?.winnerId === leftSF?.team2Id)}>
                        {tagBadge(leftSFTeam2)}
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.82rem', fontWeight: 700 }}>{leftSFTeam2?.name || 'TBD'}</div>
                            <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>Winner Group B</div>
                        </div>
                        {leftSF?.winnerId === leftSF?.team2Id && leftSF?.winnerId && <Crown size={12} style={{ color: 'var(--success)' }} />}
                    </div>
                </div>

                {/* Center: Grand Final + Champion */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '0 8px' }}>
                    <div style={{
                        padding: '4px 12px', borderRadius: 20,
                        background: 'linear-gradient(135deg, rgba(245,158,11,0.1), rgba(99,102,241,0.1))',
                        border: '1px solid rgba(245,158,11,0.25)',
                        fontSize: '0.72rem', fontWeight: 800, color: 'var(--warning)', textTransform: 'uppercase', letterSpacing: 1.5,
                    }}>
                        Grand Final
                    </div>
                    {final && (
                        <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                            {formatMatchDateTime(final.scheduledDate, final.scheduledTime)}
                        </div>
                    )}

                    {/* Connector arrows */}
                    <div style={{ display: 'flex', gap: 4 }}>
                        <div style={{ width: 40, height: 2, background: 'rgba(99,102,241,0.3)', alignSelf: 'center' }} />
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', alignSelf: 'center' }} />
                        <div style={{ width: 40, height: 2, background: 'rgba(99,102,241,0.3)', alignSelf: 'center' }} />
                    </div>

                    <div style={koBoxStyle(finalTeam1, final.winnerId === final.team1Id)}>
                        {tagBadge(finalTeam1)}
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.82rem', fontWeight: 700 }}>{finalTeam1?.name || 'TBD'}</div>
                        </div>
                        {final.winnerId === final.team1Id && <Crown size={12} style={{ color: 'var(--success)' }} />}
                    </div>
                    <div style={{
                        width: 32, height: 32, borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(245,158,11,0.15), rgba(99,102,241,0.15))',
                        border: '2px solid rgba(245,158,11,0.3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Swords size={14} style={{ color: 'var(--warning)' }} />
                    </div>
                    <div style={koBoxStyle(finalTeam2, final.winnerId === final.team2Id)}>
                        {tagBadge(finalTeam2)}
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.82rem', fontWeight: 700 }}>{finalTeam2?.name || 'TBD'}</div>
                        </div>
                        {final.winnerId === final.team2Id && <Crown size={12} style={{ color: 'var(--success)' }} />}
                    </div>

                    {/* Champion */}
                    {champion ? (
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.3 }}
                            style={{
                                textAlign: 'center', padding: '14px 20px', borderRadius: 12,
                                background: 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(16,185,129,0.08))',
                                border: '2px solid rgba(245,158,11,0.3)', marginTop: 4,
                            }}
                        >
                            <div style={{ fontSize: '1.4rem', marginBottom: 2 }}>🏆</div>
                            <div style={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '1rem', color: 'var(--warning)' }}>{champion.name}</div>
                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Champion</div>
                        </motion.div>
                    ) : (
                        <div style={{
                            textAlign: 'center', padding: '14px 20px', borderRadius: 12,
                            background: 'rgba(148,163,184,0.03)', border: '1px dashed rgba(148,163,184,0.2)', marginTop: 4, opacity: 0.6,
                        }}>
                            <div style={{ fontSize: '1.4rem', marginBottom: 2 }}>🏆</div>
                            <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-muted)' }}>Champion</div>
                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>TBD</div>
                        </div>
                    )}
                </div>

                {/* Right Column: Right Semifinal */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ textAlign: 'center', fontSize: '0.72rem', fontWeight: 700, color: 'var(--accent-hover)', textTransform: 'uppercase', letterSpacing: 1.5 }}>
                        Semifinal 2
                    </div>
                    {rightSF && (
                        <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textAlign: 'center' }}>
                            {formatMatchDateTime(rightSF.scheduledDate, rightSF.scheduledTime)}
                        </div>
                    )}
                    <div style={koBoxStyle(rightSFTeam1, rightSF?.winnerId === rightSF?.team1Id)}>
                        {tagBadge(rightSFTeam1)}
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.82rem', fontWeight: 700 }}>{rightSFTeam1?.name || 'TBD'}</div>
                            <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>Winner Group C</div>
                        </div>
                        {rightSF?.winnerId === rightSF?.team1Id && rightSF?.winnerId && <Crown size={12} style={{ color: 'var(--success)' }} />}
                    </div>
                    <div style={{ textAlign: 'center', fontSize: '0.68rem', color: 'var(--text-muted)' }}>VS</div>
                    <div style={koBoxStyle(rightSFTeam2, rightSF?.winnerId === rightSF?.team2Id)}>
                        {tagBadge(rightSFTeam2)}
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.82rem', fontWeight: 700 }}>{rightSFTeam2?.name || 'TBD'}</div>
                            <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>Winner Group D</div>
                        </div>
                        {rightSF?.winnerId === rightSF?.team2Id && rightSF?.winnerId && <Crown size={12} style={{ color: 'var(--success)' }} />}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── Scramble Countdown ─────────────────────────────────────────────────────

function ScrambleCountdown({ scrambleTime }: { scrambleTime: string }) {
    const diff = new Date(scrambleTime).getTime() - Date.now();
    if (diff <= 0) return <span style={{ color: 'var(--warning)', fontWeight: 600 }}>Registration closed. Draw processing...</span>;
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const timeStr = d > 0 ? `${d}d ${h}h ${m}m` : `${h}h ${m}m`;

    return (
        <div style={{
            textAlign: 'center', padding: '24px 20px',
            background: 'linear-gradient(135deg, rgba(99,102,241,0.04), rgba(245,158,11,0.04))',
            border: '1px dashed rgba(99,102,241,0.25)',
            borderRadius: 12,
        }}>
            <div style={{
                width: 56, height: 56, borderRadius: '50%',
                background: 'rgba(99,102,241,0.1)', border: '2px solid rgba(99,102,241,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 12px', fontSize: '1.5rem',
            }}>
                🎲
            </div>
            <div style={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '1.1rem', marginBottom: 4 }}>Group Draw Countdown</div>
            <div style={{ fontSize: '0.88rem', color: 'var(--text-muted)', marginBottom: 12 }}>
                Teams will be randomly assigned to groups in
            </div>
            <div style={{
                fontFamily: 'monospace', fontSize: '1.6rem', fontWeight: 800,
                background: 'linear-gradient(135deg, var(--accent), var(--warning))',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
                {timeStr}
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 8 }}>
                Once the draw is complete, you'll receive a notification with your match schedule.
            </div>
        </div>
    );
}

// ─── Match Schedule Section ─────────────────────────────────────────────────

function MatchSchedule({ bracket }: { bracket: MobaBracketData }) {
    const allMatches = [
        ...bracket.groupMatches.map(m => ({ ...m, type: 'group' as const })),
        ...bracket.knockoutMatches.map(m => ({
            ...m, type: 'knockout' as const,
            groupLabel: '',
            roundLabel: (m as MobaKnockoutMatch).label,
        })),
    ];

    // Group by matchDay
    const byDay = new Map<number, typeof allMatches>();
    allMatches.forEach(m => {
        const day = m.matchDay;
        if (!byDay.has(day)) byDay.set(day, []);
        byDay.get(day)!.push(m);
    });

    const sortedDays = Array.from(byDay.keys()).sort((a, b) => a - b);

    return (
        <div>
            {sortedDays.map(day => {
                const dayMatches = byDay.get(day)!;
                const allDone = dayMatches.every(m => m.winnerId !== null);
                const someStarted = dayMatches.some(m => m.winnerId !== null);
                return (
                    <div key={day} style={{ marginBottom: 16 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <div style={{
                                padding: '3px 10px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700,
                                background: allDone ? 'rgba(16,185,129,0.1)' : someStarted ? 'rgba(245,158,11,0.1)' : 'rgba(99,102,241,0.08)',
                                color: allDone ? 'var(--success)' : someStarted ? 'var(--warning)' : 'var(--accent-hover)',
                                border: allDone ? '1px solid rgba(16,185,129,0.2)' : '1px solid transparent',
                            }}>
                                Day {day}
                            </div>
                            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                                {dayMatches[0].scheduledDate && new Date(dayMatches[0].scheduledDate + 'T00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                            </span>
                            {allDone && <span style={{ fontSize: '0.68rem', color: 'var(--success)' }}>✓ Complete</span>}
                        </div>
                        {dayMatches.map(match => (
                            <MatchRow key={match.id} match={match as MobaGroupMatch | MobaKnockoutMatch} teams={bracket.teams} type={match.type} />
                        ))}
                    </div>
                );
            })}
        </div>
    );
}


// ═══════════════════════════════════════════════════════════════════════════
//   MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function WildRiftDetail({ tournament: t }: Props) {
    const { showAlert } = useModal();
    const { requireUnlock } = useWalletLock();
    const { user } = useAuth();
    const { squads: mySquads } = useMySquads(user?.uid);
    
    // Live hooks
    const { hasJoined: realHasJoined } = useHasJoined(user?.uid, t.id);
    const { wallet } = useWalletData(user?.uid);
    const hasJoined = realHasJoined;
    const walletBalance = wallet.balance;

    const [copiedField, setCopiedField] = useState<string | null>(null);

    const handleCopy = (text: string, field: string) => {
        navigator.clipboard.writeText(text);
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 2000);
    };

    const [activeTab, setActiveTab] = useState<'overview' | 'teams' | 'groups' | 'bracket' | 'schedule' | 'mvp'>('overview');
    const isScrambled = t.scrambled && t.bracketData;
    const bracket = t.bracketData;

    // Live entries for roster visibility
    const { entries: liveEntries } = useTournamentEntries(t.id);
    const isRegistering = t.status === 'registering' || t.status === 'open';
    const fillPct = Math.round((t.slotFill / t.maxSlots) * 100);

    const [showCancelModal, setShowCancelModal] = useState(false);
    const [canceling, setCanceling] = useState(false);
    const [cancelError, setCancelError] = useState<string | null>(null);

    const [showJoinModal, setShowJoinModal] = useState(false);
    const [joining, setJoining] = useState(false);
    const [joinError, setJoinError] = useState<string | null>(null);


    const cutoffHours = 24;
    const msUntilStart = new Date(t.startTime).getTime() - Date.now();
    const canCancel = msUntilStart > cutoffHours * 3600 * 1000;

    const handleConfirmCancel = async () => {
        setCanceling(true);
        setCancelError(null);
        try {
            const result = await callLeaveTournament(t.id);
            if (result.success) {
                setShowCancelModal(false);
                showAlert(`Registration cancelled successfully! T ${t.entryFee} has been refunded to your wallet.`, 'Success');
            } else {
                setCancelError(result.message || 'Failed to cancel registration.');
            }
        } catch (err: unknown) {
            setCancelError(err instanceof Error ? err.message : 'An error occurred.');
        } finally {
            setCanceling(false);
        }
    };

    const handleConfirmJoin = async () => {
        if (walletBalance < t.entryFee) {
            setJoinError('Insufficient balance to join tournament!');
            return;
        }

        setJoining(true);
        setJoinError(null);
        try {
            const squadId = t.mode !== 'Solo' ? mySquads.find(s => s.game === t.game)?.id : undefined;
            const result = await callJoinTournament(t.id, squadId);
            if (result.success) {
                setShowJoinModal(false);
                showAlert(`Successfully joined ${t.title}!`, 'Success');
            } else {
                setJoinError(result.message || 'Failed to join tournament.');
            }
        } catch (err: unknown) {
            setJoinError(err instanceof Error ? err.message : 'Failed to join tournament. Please try again.');
        } finally {
            setJoining(false);
        }
    };

    const handleJoinClick = () => {
        if (t.mode !== 'Solo') {
            const playerSquad = mySquads.find(s => s.game === t.game);
            if (!playerSquad) {
                showAlert(`You must be in a ${t.game} squad to join a ${t.mode} tournament.\nPlease create a squad in your Profile first.`, 'Squad Required');
                return;
            }
            if (playerSquad.status === 'forming') {
                showAlert("Your squad is not fully formed yet! All members must accept their invitations before you can register for a tournament.", 'Squad Forming');
                return;
            }
            
            const uid = user?.uid || '';
            const isLeader = playerSquad.members.some(m => m.uid === uid && m.role === 'leader');
            const isCoLeader = playerSquad.members.some(m => m.uid === uid && m.role === 'co-leader');
            if (!isLeader && !isCoLeader) {
                showAlert(`Only the Squad Leader or Co-Leader can join tournaments on behalf of the squad.`, 'Permission Denied');
                return;
            }
        }

        // ── Wallet Lock Gate ──
        requireUnlock(() => {
            setShowJoinModal(true);
        });
    };

    // Tab buttons
    const tabs = useMemo(() => {
        const base: { key: typeof activeTab; label: string; icon: React.ReactNode }[] = [
            { key: 'overview', label: 'Overview', icon: <Shield size={14} /> },
        ];
        // Teams tab shows when bracket is generated or tournament has entries
        if (isScrambled || t.status === 'registering' || t.status === 'open' || t.status === 'upcoming') {
            base.push(
                { key: 'teams', label: 'Teams', icon: <Users size={14} /> },
            );
        }
        if (isScrambled) {
            base.push(
                { key: 'groups', label: 'Groups', icon: <Swords size={14} /> },
                { key: 'bracket', label: 'Bracket', icon: <Trophy size={14} /> },
                { key: 'schedule', label: 'Schedule', icon: <Calendar size={14} /> },
                { key: 'mvp', label: 'MVP', icon: <Crown size={14} /> },
            );
        }
        return base;
    }, [isScrambled, t.status]);

    return (
        <div className="page-wrapper" style={{ maxWidth: 860 }}>
            <NavLink to="/tournaments" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: '0.88rem', marginBottom: 20 }}>
                <ArrowLeft size={16} /> Back to Tournaments
            </NavLink>

            {/* ── Header ── */}
            <div className="card animate-in" style={{ marginBottom: 20, position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: 'var(--accent-gradient)' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, paddingTop: 8 }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div className="mobile-hide-swords" style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(56,189,248,0.12)', border: '1px solid rgba(56,189,248,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', marginTop: 4 }}>⚔️</div>
            <div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                    <span className="tc-game-badge wildrift" style={{ display: 'inline-flex', whiteSpace: 'nowrap' }}>
                        <span className="desktop-inline">{t.game}</span>
                        <span className="mobile-inline" style={{ display: 'none' }}>WR</span>
                        {' • ' + t.mode}
                    </span>
                    {t.groupCount && (
                        <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: '0.72rem', fontWeight: 700, background: 'rgba(99,102,241,0.08)', color: 'var(--accent-hover)', border: '1px solid rgba(99,102,241,0.15)', whiteSpace: 'nowrap' }}>
                            {t.groupCount} Groups
                        </span>
                    )}
                </div>
                            <h1 style={{ fontFamily: 'Outfit', fontSize: '1.6rem', fontWeight: 800 }}>{t.title}</h1>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                                <span style={{ fontSize: '0.75rem', fontFamily: 'monospace', background: 'rgba(255,255,255,0.05)', padding: '2px 6px', borderRadius: 4, color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>
                                    {t.id}
                                </span>
                                <button onClick={(e) => { e.preventDefault(); handleCopy(t.id, 'tourneyId'); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: copiedField === 'tourneyId' ? 'var(--success)' : 'var(--text-muted)', display: 'flex', alignItems: 'center', padding: 2, transition: 'color 0.2s' }} title="Copy Tournament ID">
                                    {copiedField === 'tourneyId' ? <Check size={14} /> : <Copy size={14} />}
                                </button>
                            </div>
                            <p style={{ color: 'var(--text-muted)', marginTop: 4, fontSize: '0.88rem' }}>
                                {new Date(t.startTime).toLocaleString('en-US', { weekday: 'long', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                        </div>
                    </div>
                    <span className={`tc-status ${t.status}`} style={{ fontSize: '0.85rem', padding: '6px 14px' }}>
                        {t.status === 'live' && <span className="pulse-dot" />}
                        {t.status.charAt(0).toUpperCase() + t.status.slice(1)}
                    </span>
                </div>
                <div className="tc-countdown" style={{ fontSize: '0.92rem' }}>
                    <Clock size={16} /> {t.status === 'live' ? 'Tournament in progress' : `Starts in ${getTimeLeft(t.startTime, t.status)}`}
                </div>
            </div>

            {/* ── Stats ── */}
            <div className="responsive-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
                {[
                    { icon: <Trophy size={18} />, label: 'Prize Pool', value: <><TCoinIcon size={18} /> {Math.round(t.slotFill * t.entryFee * 0.70).toLocaleString()}</>, color: 'var(--success)' },
                    { icon: <Clock size={18} />, label: 'Entry Fee', value: <><TCoinIcon size={18} /> {t.entryFee}</>, color: 'var(--warning)' },
                    { icon: <Shield size={18} />, label: 'Format', value: `BO3`, color: 'var(--neon-blue)' },
                    { icon: <Swords size={18} />, label: 'Teams', value: `${t.slotFill}/${t.maxSlots}`, color: 'var(--accent-hover)' },
                ].map((s, i) => (
                    <div key={i} className={`stat-card animate-in stagger-${i + 1}`} style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <div style={{ color: s.color, marginBottom: 8 }}>{s.icon}</div>
                        <div className="stat-label">{s.label}</div>
                        <div className="stat-value" style={{ fontSize: '1.3rem', color: s.color, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px', marginTop: 'auto' }}>{s.value}</div>
                    </div>
                ))}
            </div>

            {/* ── Slot Bar ── */}
            <div className="card animate-in stagger-2" style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Slot Fill</span>
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{fillPct}%</span>
                </div>
                <div className="tc-slots-bar" style={{ height: 10, borderRadius: 5 }}>
                    <motion.div className="tc-slots-fill"
                        initial={{ width: 0 }}
                        animate={{ width: `${fillPct}%` }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        style={{
                            background: fillPct >= 75 ? 'var(--success)' : fillPct >= 50 ? 'var(--warning)' : 'var(--accent)',
                            borderRadius: 5, height: '100%',
                    }} />
                </div>
                <p style={{ marginTop: 6, fontSize: '0.8rem', color: 'var(--text-muted)' }}>{t.maxSlots - t.slotFill} slot{t.maxSlots - t.slotFill !== 1 ? 's' : ''} remaining</p>
            </div>

            {/* ── Tab Bar ── */}
            <div className="animate-in stagger-3 wr-tab-bar" style={{
                marginBottom: 20, padding: 4,
                background: 'rgba(148,163,184,0.04)', borderRadius: 10, border: '1px solid var(--border)',
            }}>
                {tabs.map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        style={{
                            flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            fontSize: '0.85rem', fontWeight: 600, transition: 'all 0.2s',
                            background: activeTab === tab.key ? 'var(--accent)' : 'transparent',
                            color: activeTab === tab.key ? '#fff' : 'var(--text-secondary)',
                        }}
                    >
                        {tab.icon} {tab.label}
                    </button>
                ))}
            </div>

            {/* ── Tab Content ── */}
            <AnimatePresence mode="wait">
                {activeTab === 'overview' && (
                    <motion.div key="overview" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                        {/* Referee Details (Live/Open Matches) */}
                        {Boolean((t.status === 'live') && t.refereeIgn && t.refereeId) && (
                            <div className="card animate-in stagger-3" style={{
                                marginBottom: 20, border: '2px dashed rgba(99,102,241,0.5)',
                                background: 'linear-gradient(135deg, rgba(99,102,241,0.04), rgba(56,189,248,0.04))',
                                padding: '20px 24px',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                                    <span className="pulse-dot" style={{ background: 'var(--accent)' }} />
                                    <span style={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '0.85rem', letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--accent)' }}>
                                        Match is Live — Referee Details
                                    </span>
                                </div>
                                <div className="responsive-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                                    <div>
                                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Referee IGN</div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <span style={{ fontFamily: 'monospace', fontSize: '1.3rem', fontWeight: 800, color: 'var(--accent)', letterSpacing: 1 }}>{t.refereeIgn as React.ReactNode}</span>
                                            <button onClick={() => handleCopy(t.refereeIgn as string, 'refereeIgn')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: copiedField === 'refereeIgn' ? 'var(--success)' : 'var(--text-muted)', padding: 4, transition: 'color 0.2s' }}>
                                                {copiedField === 'refereeIgn' ? <Check size={16} /> : <Copy size={16} />}
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Game ID</div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <span style={{ fontFamily: 'monospace', fontSize: '1.3rem', fontWeight: 800, color: 'var(--accent)', letterSpacing: 1 }}>{t.refereeId as React.ReactNode}</span>
                                            <button onClick={() => handleCopy(t.refereeId as string, 'refereeId')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: copiedField === 'refereeId' ? 'var(--success)' : 'var(--text-muted)', padding: 4, transition: 'color 0.2s' }}>
                                                {copiedField === 'refereeId' ? <Check size={16} /> : <Copy size={16} />}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div style={{ marginTop: 12, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                    Player(s) should add the Referee in-game to receive custom lobby invites.
                                </div>
                            </div>
                        )}

                        {Boolean((t.status === 'live') && (!t.refereeIgn || !t.refereeId)) && (
                            <div className="card animate-in stagger-3" style={{
                                marginBottom: 20, textAlign: 'center', padding: '20px 24px',
                                background: 'rgba(148,163,184,0.04)', border: '1px solid var(--border)',
                            }}>
                                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', margin: 0 }}>⏳ Match is live — Referee details will be available shortly. Stay tuned!</p>
                            </div>
                        )}

                        {/* Game Rules (from Firestore) */}
                        <GameRulesCard game={t.game} />

                        {/* Tournament Info */}
                        {Boolean(t.matchesPerDay) && (
                            <div className="card" style={{ marginBottom: 20 }}>
                                <h3 style={{ fontFamily: 'Outfit', fontWeight: 700, marginBottom: 16 }}>⚔️ Tournament Format</h3>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                    <div style={{ padding: 12, background: 'rgba(99,102,241,0.04)', borderRadius: 8, border: '1px solid var(--border)' }}>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>Groups</div>
                                        <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{t.groupCount}</div>
                                    </div>
                                    <div style={{ padding: 12, background: 'rgba(99,102,241,0.04)', borderRadius: 8, border: '1px solid var(--border)' }}>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>Teams / Group</div>
                                        <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>4</div>
                                    </div>
                                    <div style={{ padding: 12, background: 'rgba(99,102,241,0.04)', borderRadius: 8, border: '1px solid var(--border)' }}>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>Matches / Day</div>
                                        <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{t.matchesPerDay as number}</div>
                                    </div>
                                    <div style={{ padding: 12, background: 'rgba(99,102,241,0.04)', borderRadius: 8, border: '1px solid var(--border)' }}>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>Match Format</div>
                                        <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>Best of 3</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Scramble countdown or status */}
                        {!isScrambled && t.scrambleTime && (
                            <div className="card" style={{ marginBottom: 20, padding: 0 }}>
                                <div style={{ padding: '20px' }}>
                                    <ScrambleCountdown scrambleTime={t.scrambleTime} />
                                </div>
                            </div>
                        )}

                        {isScrambled && (
                            <div className="card" style={{ marginBottom: 20, padding: '16px 20px', background: 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.15)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(16,185,129,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✅</div>
                                    <div>
                                        <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>Draw Completed</div>
                                        <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>Groups have been assigned and matches are scheduled. Check the Groups, Bracket, and Schedule tabs.</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Join Button */}
                        { (t.status === 'registering' || t.status === 'open') && !hasJoined && (
                            <div className="animate-in stagger-5" style={{ textAlign: 'center' }}>
                                <button className="btn btn-primary btn-lg" style={{ minWidth: 280, fontSize: '1.05rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', margin: '0 auto' }} onClick={handleJoinClick}>
                                    ⚡ Join Tournament —
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '1.25rem', fontWeight: 800 }}>
                                        <TCoinIcon size={24} />{t.entryFee}
                                    </span>
                                </button>
                                <p style={{ marginTop: 8, fontSize: '0.78rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                    <TCoinIcon size={14} />{t.entryFee} will be deducted from your wallet balance
                                </p>
                            </div>
                        )}
                        { (t.status === 'registration closed' || t.status === 'upcoming') && !hasJoined && (
                            <div className="animate-in stagger-5" style={{ textAlign: 'center' }}>
                                <button className="btn btn-primary btn-lg" style={{ minWidth: 280, fontSize: '1.05rem', background: 'var(--bg-glass)', borderColor: 'var(--border)', color: 'var(--error)', opacity: 0.8, pointerEvents: 'none' }} disabled>
                                    Registrations Closed
                                </button>
                                <p style={{ marginTop: 8, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                                    This tournament is no longer accepting new participants.
                                </p>
                            </div>
                        )}
                        { (t.status === 'registering' || t.status === 'registration closed' || t.status === 'open' || t.status === 'upcoming') && hasJoined && (
                            <div style={{ textAlign: 'center', display: 'flex', gap: 10, justifyContent: 'center' }}>
                                <button className="btn btn-primary btn-lg" style={{ minWidth: canCancel ? 140 : 280, fontSize: '1.05rem', background: 'var(--success)', borderColor: 'var(--success)', pointerEvents: 'none' }} disabled>
                                    ✓ Registered
                                </button>
                                {/* Only show cancel if NOT closed AND still within time cutoff */}
                                {canCancel && (t.status === 'registering' || t.status === 'open') && (
                                    <button 
                                        className="btn btn-primary btn-lg" 
                                        style={{ minWidth: 140, fontSize: '1.05rem', background: 'var(--danger)', borderColor: 'var(--danger)', color: 'white' }}
                                        onClick={() => setShowCancelModal(true)}
                                    >
                                        Cancel
                                    </button>
                                )}
                            </div>
                        )}
                    </motion.div>
                )}

                {activeTab === 'groups' && bracket && (
                    <motion.div key="groups" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                        {/* Desktop: Masonry Layout */}
                        <div className="desktop-flex-masonry" style={{ display: 'flex', gap: 14 }}>
                            {/* Left Column */}
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
                                {bracket.groups.filter((_: { label: string; teamIds: string[] }, i: number) => i % 2 === 0).map(group => (
                                    <MobaGroupCard key={group.label} group={group} teams={bracket.teams} matches={bracket.groupMatches} />
                                ))}
                            </div>
                            {/* Right Column */}
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
                                {bracket.groups.filter((_: { label: string; teamIds: string[] }, i: number) => i % 2 === 1).map(group => (
                                    <MobaGroupCard key={group.label} group={group} teams={bracket.teams} matches={bracket.groupMatches} />
                                ))}
                            </div>
                        </div>
                        {/* Mobile: Chronological List Layout */}
                        <div className="mobile-flex-col" style={{ display: 'none', flexDirection: 'column', gap: 14 }}>
                            {bracket.groups.map(group => (
                                <MobaGroupCard key={'mobile-' + group.label} group={group} teams={bracket.teams} matches={bracket.groupMatches} />
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* Teams Tab */}
                {activeTab === 'teams' && (
                    <motion.div key="teams" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                        <div className="card" style={{ padding: '20px' }}>
                            <h3 style={{ fontFamily: 'Outfit', fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                                👥 Registered Teams
                                {isRegistering && (
                                    <span style={{
                                        fontSize: '0.7rem', fontWeight: 600, padding: '3px 10px', borderRadius: 20,
                                        background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)',
                                        color: 'var(--warning)', display: 'inline-flex', alignItems: 'center', gap: 4,
                                    }}>
                                        <EyeOff size={10} /> Rosters Hidden
                                    </span>
                                )}
                                {!isRegistering && bracket && (
                                    <span style={{
                                        fontSize: '0.7rem', fontWeight: 600, padding: '3px 10px', borderRadius: 20,
                                        background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)',
                                        color: 'var(--success)', display: 'inline-flex', alignItems: 'center', gap: 4,
                                    }}>
                                        <Eye size={10} /> Rosters Revealed
                                    </span>
                                )}
                            </h3>

                            {/* During registration: show team names only (from live entries or bracket) */}
                            {isRegistering && (
                                <>
                                    {/* Live entries: group by squadName */}
                                    {liveEntries.length > 0 ? (
                                        <MobaRegistrationTeamList entries={liveEntries} />
                                    ) : bracket ? (
                                        /* Fallback to bracket teams in name-only mode */
                                        <div className="mobile-grid-1-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                            {bracket.teams.map((team) => (
                                                <MobaTeamNameCard key={team.id} name={team.name} tag={team.tag} color={team.color} />
                                            ))}
                                        </div>
                                    ) : (
                                        <div style={{
                                            textAlign: 'center', padding: '32px 20px',
                                            background: 'rgba(148,163,184,0.03)', borderRadius: 10,
                                            border: '1px dashed rgba(148,163,184,0.2)',
                                        }}>
                                            <div style={{ fontSize: '2rem', marginBottom: 8 }}>⏳</div>
                                            <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: 4 }}>Awaiting Teams</div>
                                            <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                                                Team names will appear here as squads register. Rosters will be revealed when registration closes.
                                            </div>
                                        </div>
                                    )}
                                    <div style={{
                                        marginTop: 16, padding: '10px 14px', borderRadius: 8,
                                        background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.15)',
                                        display: 'flex', alignItems: 'center', gap: 8,
                                        fontSize: '0.82rem', color: 'var(--warning)',
                                    }}>
                                        <EyeOff size={14} style={{ flexShrink: 0 }} />
                                        Individual player rosters are hidden until registration closes to prevent scouting.
                                    </div>
                                </>
                            )}

                            {/* After registration: show full rosters */}
                            {!isRegistering && bracket ? (
                                <>
                                    {/* Desktop: Masonry Layout */}
                                    <div className="desktop-flex-masonry" style={{ display: 'flex', gap: 10 }}>
                                        {/* Left column — even-indexed teams */}
                                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
                                            {bracket.teams.filter((_: MobaTeam, i: number) => i % 2 === 0).map((team) => (
                                                <ExpandableTeamCard key={team.id} team={team} />
                                            ))}
                                        </div>
                                        {/* Right column — odd-indexed teams */}
                                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
                                            {bracket.teams.filter((_: MobaTeam, i: number) => i % 2 === 1).map((team) => (
                                                <ExpandableTeamCard key={team.id} team={team} />
                                            ))}
                                        </div>
                                    </div>
                                    {/* Mobile: Chronological List Layout */}
                                    <div className="mobile-flex-col" style={{ display: 'none', flexDirection: 'column', gap: 10 }}>
                                        {bracket.teams.map((team) => (
                                            <ExpandableTeamCard key={'mobile-' + team.id} team={team} />
                                        ))}
                                    </div>
                                </>
                            ) : !isRegistering && (
                                <div style={{
                                    textAlign: 'center', padding: '32px 20px',
                                    background: 'rgba(148,163,184,0.03)', borderRadius: 10,
                                    border: '1px dashed rgba(148,163,184,0.2)',
                                }}>
                                    <div style={{ fontSize: '2rem', marginBottom: 8 }}>🎲</div>
                                    <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: 4 }}>Draw Pending</div>
                                    <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                                        Teams will be revealed after the group draw is completed.
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}

                {activeTab === 'bracket' && bracket && (
                    <motion.div key="bracket" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                        <div className="card" style={{ padding: '24px 20px', overflow: 'auto' }}>
                            <h3 style={{ fontFamily: 'Outfit', fontWeight: 700, marginBottom: 20, textAlign: 'center' }}>
                                🏆 Knockout Bracket
                            </h3>
                            <KnockoutBracket bracket={bracket} teams={bracket.teams} />
                        </div>
                    </motion.div>
                )}

                {activeTab === 'schedule' && bracket && (
                    <motion.div key="schedule" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                        <div className="card" style={{ padding: '20px' }}>
                            <h3 style={{ fontFamily: 'Outfit', fontWeight: 700, marginBottom: 16 }}>📅 Match Schedule</h3>
                            <MatchSchedule bracket={bracket} />
                        </div>
                    </motion.div>
                )}

                {activeTab === 'mvp' && bracket && (
                    <motion.div key="mvp" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                        <MvpStandings bracket={bracket} />
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Cancel Modal */}
            <AnimatePresence>
                {showCancelModal && (
                    <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        zIndex: 1000, padding: 20
                    }}>
                        <motion.div 
                            className="card animate-in"
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            style={{ maxWidth: 400, width: '100%', textAlign: 'center', padding: 32 }}
                        >
                            <AlertTriangle size={48} color="var(--warning)" style={{ margin: '0 auto 16px' }} />
                            <h2 style={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '1.4rem', marginBottom: 16 }}>Cancel Registration?</h2>
                            <p style={{ color: 'var(--text-muted)', marginBottom: 20 }}>
                                Are you sure you want to cancel your registration for <strong>{t.title}</strong>? 
                                <br/><br/>
                                Your entry fee of <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><TCoinIcon size={14} /> {t.entryFee}</span> will be refunded to your wallet.
                            </p>
                            
                            {cancelError && (
                                <div style={{ color: 'white', background: 'var(--danger)', padding: '10px', borderRadius: 8, marginBottom: 20 }}>
                                    {cancelError}
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                                <button className="btn btn-primary" style={{ flex: 1, background: 'var(--success)', borderColor: 'var(--success)', color: 'white' }} onClick={() => setShowCancelModal(false)} disabled={canceling}>
                                    Keep Registration
                                </button>
                                <button className="btn btn-primary" style={{ flex: 1, background: 'var(--danger)', borderColor: 'var(--danger)', color: 'white' }} onClick={handleConfirmCancel} disabled={canceling}>
                                    {canceling ? 'Canceling...' : 'Yes, Cancel'}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Join Modal */}
            <AnimatePresence>
                {showJoinModal && (
                    <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        zIndex: 1000, padding: 20
                    }}>
                        <motion.div 
                            className="card animate-in"
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            style={{ maxWidth: 400, width: '100%', textAlign: 'center', padding: 32 }}
                        >
                            <h2 style={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '1.4rem', marginBottom: 16 }}>Confirm Registration</h2>
                            <p style={{ color: 'var(--text-muted)', marginBottom: 20 }}>
                                Are you sure you want to join <strong>{t.title}</strong>? <br/>
                                This action will reserve your slot for the tournament.
                            </p>

                            <div style={{ background: 'var(--bg-glass)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 16, marginBottom: 24, textAlign: 'left' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: '0.9rem' }}>
                                    <span style={{ color: 'var(--text-muted)' }}>Current Balance</span>
                                    <span style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}><TCoinIcon size={14} /> {walletBalance.toLocaleString()}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, fontSize: '0.9rem' }}>
                                    <span style={{ color: 'var(--text-muted)' }}>Joining Fee</span>
                                    <span style={{ fontWeight: 600, color: 'var(--warning)' }}>-T {t.entryFee}</span>
                                </div>
                                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontWeight: 600 }}>Remaining Balance</span>
                                    <span style={{ fontWeight: 700, color: 'var(--success)' }}>T {(walletBalance - t.entryFee).toLocaleString()}</span>
                                </div>
                            </div>
                            
                            {joinError && (
                                <div style={{ color: 'white', background: 'var(--danger)', padding: '10px', borderRadius: 8, marginBottom: 20 }}>
                                    {joinError}
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                                <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowJoinModal(false)} disabled={joining}>
                                    No, Cancel
                                </button>
                                <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleConfirmJoin} disabled={joining}>
                                    {joining ? 'Joining...' : 'Yes, Join Now'}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}

// ─── MOBA Team Name Card (Registration Mode — Name Only) ────────────────────

function MobaTeamNameCard({ name, tag, color }: { name: string; tag: string; color: string }) {
    return (
        <div style={{
            padding: '10px 14px', borderRadius: 10,
            background: 'rgba(148,163,184,0.04)', border: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', gap: 10,
            transition: 'border-color 0.2s',
        }}>
            <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: `${color}15`, border: `2px solid ${color}40`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.6rem', fontWeight: 900, color: color, letterSpacing: 0.5,
            }}>
                {tag}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '0.88rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {name}
                </div>
            </div>
            <EyeOff size={12} style={{ color: 'var(--text-muted)', opacity: 0.5 }} />
        </div>
    );
}

// ─── MOBA Registration Team List (Groups live entries by squadName) ─────────

function MobaRegistrationTeamList({ entries }: { entries: { squadName: string; squadId: string | null }[] }) {
    // Group entries by squadName
    const teamMap = new Map<string, number>();
    entries.forEach(e => {
        const key = e.squadName || 'Unknown Team';
        teamMap.set(key, (teamMap.get(key) || 0) + 1);
    });

    const teams = Array.from(teamMap.entries());

    if (teams.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                No teams registered yet.
            </div>
        );
    }

    // Generate consistent colors from team name
    const hashColor = (str: string): string => {
        let hash = 0;
        for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
        const h = hash % 360;
        return `hsl(${h < 0 ? h + 360 : h}, 65%, 55%)`;
    };

    return (
        <div className="mobile-grid-1-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {teams.map(([name, count]) => (
                <div key={name} style={{
                    padding: '10px 14px', borderRadius: 10,
                    background: 'rgba(148,163,184,0.04)', border: '1px solid var(--border)',
                    display: 'flex', alignItems: 'center', gap: 10,
                }}>
                    <div style={{
                        width: 32, height: 32, borderRadius: 8,
                        background: `${hashColor(name)}15`, border: `2px solid ${hashColor(name)}40`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.55rem', fontWeight: 900, color: hashColor(name),
                    }}>
                        {name.split(' ').map(w => w[0]).join('').slice(0, 3).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {name}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                            {count} member{count !== 1 ? 's' : ''} registered
                        </div>
                    </div>
                    <EyeOff size={12} style={{ color: 'var(--text-muted)', opacity: 0.5 }} />
                </div>
            ))}
        </div>
    );
}

// ─── MOBA Role Icons ────────────────────────────────────────────────────────

const ROLE_ICONS: Record<string, { emoji: string; color: string }> = {
    Top:     { emoji: '🛡️', color: '#f59e0b' },
    Jungle:  { emoji: '🌿', color: '#10b981' },
    Mid:     { emoji: '⚡', color: '#6366f1' },
    ADC:     { emoji: '🎯', color: '#ef4444' },
    Support: { emoji: '💚', color: '#22d3ee' },
};

// ─── Expandable Team Card ───────────────────────────────────────────────────

function ExpandableTeamCard({ team }: { team: MobaTeam }) {
    const [expanded, setExpanded] = useState(false);

    return (
        <motion.div
            className="card"
            style={{
                padding: '14px 16px', cursor: 'pointer', overflow: 'hidden',
                position: 'relative', transition: 'border-color 0.2s',
            }}
            onClick={() => setExpanded(!expanded)}
        >
            {/* Color bar */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `${team.color}80` }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 2 }}>
                <div style={{
                    width: 36, height: 36, borderRadius: 8,
                    background: `${team.color}15`, border: `2px solid ${team.color}40`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.65rem', fontWeight: 900, color: team.color, letterSpacing: 0.5,
                }}>
                    {team.tag}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {team.name}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Crown size={10} style={{ color: 'var(--warning)' }} />
                        {team.captain}
                    </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                    <span style={{
                        fontSize: '0.65rem', fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                        background: 'rgba(99,102,241,0.08)', color: 'var(--accent-hover)',
                    }}>
                        {team.rank}
                    </span>
                    {expanded ? <ChevronUp size={12} style={{ color: 'var(--text-muted)' }} /> : <ChevronDown size={12} style={{ color: 'var(--text-muted)' }} />}
                </div>
            </div>

            <AnimatePresence>
                {expanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        style={{ overflow: 'hidden', marginTop: 10 }}
                    >
                        <div style={{ padding: '10px 12px', background: 'rgba(148,163,184,0.03)', borderRadius: 8, border: '1px solid var(--border)' }}>
                            {/* Region */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>
                                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Region</span>
                                <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>{team.region}</span>
                            </div>

                            {/* Roster Header */}
                            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
                                Roster ({team.members?.length || 0} players)
                            </div>

                            {/* Player List */}
                            {team.members && team.members.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    {team.members.map((memberRaw, i) => {
                                        const member = typeof memberRaw === 'string' ? { ign: memberRaw, role: 'Member' as any } : memberRaw;
                                        const isCaptain = member.ign === team.captain;
                                        const roleInfo = ROLE_ICONS[member.role] || { emoji: '❓', color: '#94a3b8' };
                                        return (
                                            <div key={i} style={{
                                                display: 'flex', alignItems: 'center', gap: 8,
                                                padding: '5px 8px', borderRadius: 6,
                                                background: isCaptain ? 'rgba(245,158,11,0.06)' : 'transparent',
                                                border: isCaptain ? '1px solid rgba(245,158,11,0.15)' : '1px solid transparent',
                                            }}>
                                                {/* Role Icon */}
                                                <span style={{ fontSize: '0.75rem', width: 20, textAlign: 'center' }}>{roleInfo.emoji}</span>

                                                {/* Role Label */}
                                                <span style={{
                                                    fontSize: '0.62rem', fontWeight: 700, width: 46,
                                                    color: roleInfo.color, textTransform: 'uppercase', letterSpacing: 0.3,
                                                }}>
                                                    {member.role}
                                                </span>

                                                {/* IGN */}
                                                <span style={{
                                                    flex: 1, fontSize: '0.78rem', fontWeight: isCaptain ? 700 : 500,
                                                    color: isCaptain ? 'var(--text-primary)' : 'var(--text-secondary)',
                                                }}>
                                                    {member.ign}
                                                </span>

                                                {/* Captain Badge */}
                                                {isCaptain && (
                                                    <span style={{
                                                        fontSize: '0.58rem', fontWeight: 800,
                                                        padding: '1px 5px', borderRadius: 3,
                                                        background: 'rgba(245,158,11,0.12)', color: 'var(--warning)',
                                                        display: 'flex', alignItems: 'center', gap: 2,
                                                    }}>
                                                        <Crown size={8} /> CPT
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {team.substitute && (
                                        <>
                                            <div style={{ height: 1, background: 'rgba(255,255,255,0.05)', margin: '4px 0' }} />
                                            <div style={{
                                                display: 'flex', alignItems: 'center', gap: 8,
                                                padding: '5px 8px', borderRadius: 6,
                                                background: 'transparent',
                                                border: '1px solid transparent',
                                            }}>
                                                <span style={{ fontSize: '0.75rem', width: 20, textAlign: 'center' }}>🔄</span>
                                                <span style={{
                                                    fontSize: '0.62rem', fontWeight: 700, width: 46,
                                                    color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.3,
                                                }}>
                                                    SUB
                                                </span>
                                                <span style={{
                                                    flex: 1, fontSize: '0.78rem', fontWeight: 500,
                                                    color: 'var(--text-secondary)',
                                                }}>
                                                    {team.substitute.ign}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ) : (
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic', padding: '8px 0' }}>
                                    Roster not yet available
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}

// ─── MVP Standings Component ────────────────────────────────────────────────

function MvpStandings({ bracket }: { bracket: MobaBracketData }) {
    const mvpTally = bracket.mvpTally;
    const allMatches = [...bracket.groupMatches, ...bracket.knockoutMatches];
    const allComplete = allMatches.every(m => m.winnerId !== null);
    const tournamentMvpId = bracket.tournamentMvpId || null;

    // Find tournament MVP details from tally (now Object/Map keyed by playerId)
    const tournamentMvpEntry = tournamentMvpId && mvpTally && mvpTally[tournamentMvpId]
        ? mvpTally[tournamentMvpId]
        : null;

    // Convert Object/Map to sorted array descending by count
    const sorted = useMemo(() => {
        if (!mvpTally || Object.keys(mvpTally).length === 0) return [] as { playerId: string; playerName: string; squadId?: string; teamId?: string; squadName?: string; count: number }[];
        return Object.entries(mvpTally)
            .map(([playerId, entry]) => ({ playerId, ...entry }))
            .sort((a, b) => b.count - a.count);
    }, [mvpTally]);

    return (
        <div className="card" style={{ padding: '24px 20px' }}>
            <h3 style={{ fontFamily: 'Outfit', fontWeight: 700, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
                🏆 MVP Standings
            </h3>

            {/* Tournament MVP Banner */}
            {tournamentMvpEntry && allComplete && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4 }}
                    style={{
                        padding: '16px 20px', borderRadius: 12, marginBottom: 20,
                        background: 'linear-gradient(135deg, rgba(245,158,11,0.12), rgba(234,179,8,0.08))',
                        border: '2px solid rgba(245,158,11,0.3)',
                        textAlign: 'center',
                    }}
                >
                    <div style={{ fontSize: '1.8rem', marginBottom: 4 }}>🏆</div>
                    <div style={{
                        fontFamily: 'Outfit', fontWeight: 800, fontSize: '1.15rem',
                        background: 'linear-gradient(135deg, #f59e0b, #eab308)',
                        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                        marginBottom: 4,
                    }}>
                        Tournament MVP: {tournamentMvpEntry.playerName}
                    </div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                        from <strong>{tournamentMvpEntry.squadName}</strong> with <strong>{tournamentMvpEntry.count}</strong> MVP{tournamentMvpEntry.count !== 1 ? 's' : ''}
                    </div>
                </motion.div>
            )}

            {/* MVP List */}
            {sorted.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {sorted.map((entry, i) => {
                        const rank = i + 1;
                        const team = getTeamById(bracket.teams, entry.squadId || entry.teamId || '');
                        return (
                            <motion.div
                                key={entry.playerName}
                                initial={{ opacity: 0, x: -12 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: i * 0.06 }}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 10,
                                    padding: '10px 14px', borderRadius: 10,
                                    background: rank === 1
                                        ? 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(234,179,8,0.04))'
                                        : 'rgba(148,163,184,0.04)',
                                    border: rank === 1
                                        ? '1px solid rgba(245,158,11,0.25)'
                                        : '1px solid var(--border)',
                                    transition: 'all 0.2s',
                                }}
                            >
                                {/* Rank */}
                                <div style={{
                                    width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                                    background: rank === 1
                                        ? 'rgba(245,158,11,0.15)'
                                        : rank === 2 ? 'rgba(148,163,184,0.1)'
                                        : rank === 3 ? 'rgba(205,127,50,0.1)'
                                        : 'rgba(148,163,184,0.05)',
                                    border: rank === 1
                                        ? '1px solid rgba(245,158,11,0.3)'
                                        : '1px solid var(--border)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: rank === 1 ? '0.85rem' : '0.72rem',
                                    fontWeight: 800,
                                    color: rank === 1 ? '#f59e0b' : 'var(--text-muted)',
                                }}>
                                    {rank === 1 ? '🏆' : `#${rank}`}
                                </div>

                                {/* Team tag badge */}
                                {team && (
                                    <div style={{
                                        width: 26, height: 26, borderRadius: 6, flexShrink: 0,
                                        background: `${team.color}20`, border: `1px solid ${team.color}40`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '0.55rem', fontWeight: 800, color: team.color,
                                    }}>
                                        {team.tag}
                                    </div>
                                )}

                                {/* Player info */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{
                                        fontWeight: 700, fontSize: '0.88rem',
                                        color: rank === 1 ? '#f59e0b' : 'var(--text-primary)',
                                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                    }}>
                                        {entry.playerName}
                                    </div>
                                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                                        {entry.squadName || team?.name}
                                    </div>
                                </div>

                                {/* MVP count badge */}
                                <div style={{
                                    padding: '3px 10px', borderRadius: 6, flexShrink: 0,
                                    background: 'rgba(245,158,11,0.1)',
                                    border: '1px solid rgba(245,158,11,0.2)',
                                    fontSize: '0.78rem', fontWeight: 800, color: '#f59e0b',
                                }}>
                                    ×{entry.count}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            ) : (
                <div style={{
                    textAlign: 'center', padding: '32px 20px',
                    background: 'rgba(148,163,184,0.03)', borderRadius: 10,
                    border: '1px dashed rgba(148,163,184,0.2)',
                }}>
                    <div style={{ fontSize: '2rem', marginBottom: 8 }}>🌟</div>
                    <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: 4 }}>No MVPs Yet</div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                        MVP standings will appear as matches are completed.
                    </div>
                </div>
            )}
        </div>
    );
}

